<script lang="ts" setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import Quill from 'quill';
import type { QuillEditor as QuillEditorType } from '@vueup/vue-quill';
import { trans, transchoice } from '../../_00_elements/translation';
import HorizontalList from '../../_02_molecules/components/HorizontalList.vue';
import { type UUIDv4 } from '../../types/uuid';
import Button from './Button.vue';

declare global {
  interface Window {
    USERFILE_PREVIEWABLE_MIMETYPES: string[];
  }
}

interface Attachment {
  uuid: UUIDv4;
  name: string;
  mimetype: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    disableLink?: boolean;
    showSubmit?: boolean;
    minCounter?: number;
    maxCounter?: number;
    placeholder?: string;
    loading?: boolean;
    attachments?: Attachment[];
  }>(),
  {
    disableLink: false,
    showSubmit: false,
    minCounter: undefined,
    maxCounter: undefined,
    placeholder: undefined,
    loading: false,
    attachments: () => [],
  },
);

const emit = defineEmits<{(e: 'update:modelValue', payload: string): void;
  (e: 'removeAttachment', index: number): void;
  (e: 'submit'): void;
}>();

const instance = getCurrentInstance();
const uid = String(instance?.uid ?? Math.floor(Math.random() * 10000));
const toolbarId = `quill-toolbar-${uid}`;

const html = computed<string>({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const quillRef = ref<InstanceType<typeof QuillEditorType>>();
const quillInstance = computed<Quill>(() => quillRef.value!.getQuill());

const text = computed<string>(() => html.value.replace(/<[^>]*>|\r?\n/g, '').trim());

const hasCounter = computed<boolean>(() => props.maxCounter !== undefined || props.minCounter !== undefined);
const counter = computed<number>(() => text.value.length);
const canSubmit = computed<boolean>(() => (text.value.length > 0 || props.attachments.length > 0) && props.loading === false);

const watchModelValue = (): void => {
  watch(() => props.modelValue, (value) => {
    if (value.trim().length === 0) {
      quillInstance.value?.setText('');
    }
  });
};

const setupLinkTool = (): void => {
  if (props.disableLink) {
    return;
  }
  const toolbar = quillInstance.value.getModule('toolbar');
  toolbar.handlers.link = (): void => {
    const range = quillInstance.value.getSelection();
    if (!range) {
      return;
    }
    // eslint-disable-next-line no-alert
    const url = prompt(trans('content_editor.link.message'));
    if (url && /^https?:\/\//i.test(url)) {
      quillInstance.value.format('link', url);
    } else if (url) {
      // eslint-disable-next-line no-alert
      alert(trans('content_editor.link.error'));
    }
  };
};

const setupKeyListeners = (): void => {
  quillInstance.value.root.addEventListener('keydown', (e: KeyboardEvent) => {
    // Hack for implementation on search
    if (e.key === 'Enter') {
      setTimeout(() => quillInstance.value.focus(), 0);
    }
  });
};

function pastedHtmlToText(pastedHtml: string) {
  return pastedHtml
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/^\s*\n+/, '')
    .replace(/\n+\s*$/, '');
}

const setupClipboard = (): void => {
  const clipboard = quillInstance.value.getModule('clipboard');
  clipboard.addMatcher(Node.ELEMENT_NODE, (node: Node) => {
    const textContent = node.textContent || '';
    const Delta = Quill.import('delta');
    return new Delta().insert(textContent);
  });

  quillInstance.value.root.addEventListener('paste', (e: ClipboardEvent) => {
    e.preventDefault();
    const clipboardHtml = e.clipboardData?.getData('text/html');
    if (!clipboardHtml) {
      return;
    }
    const quill = quillInstance.value;
    quill.focus();
    const selection = quill.getSelection(true);
    const insertAt = selection?.index ?? quill.getLength();
    const clipboardText = pastedHtmlToText(clipboardHtml);
    setTimeout(() => {
      quill.insertText(insertAt, clipboardText, 'api');
      setTimeout(() => {
        quill.setSelection(insertAt + clipboardText.length, 0, 'silent');
      }, 25);
    }, 25);
  });
};

const quillReady = (): void => {
  watchModelValue();
  setupLinkTool();
  setupKeyListeners();
  setupClipboard();
};

const isFileAnImage = (file: Attachment): boolean => Object.values(window.USERFILE_PREVIEWABLE_MIMETYPES).includes(file.mimetype);

const removeAttachment = (index: number) => {
  emit('removeAttachment', index);
};

const submit = () => {
  emit('submit');
};
</script>

<template>
  <div class="content-vue-editor">
    <div
      class="content-vue-editor__quill editor-quill"
      :class="{ 'editor-quill--attachments': attachments.length > 0 }"
    >
      <QuillEditor
        ref="quillRef"
        v-model:content="html"
        :placeholder="placeholder"
        content-type="html"
        theme="snow"
        :toolbar="`#${toolbarId}`"
        @ready="quillReady"
      >
        <template #toolbar>
          <div :id="toolbarId" class="editor__toolbar">
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
            <button class="ql-list" value="bullet" />
            <button v-if="!disableLink" class="ql-link" />
          </div>
        </template>
      </QuillEditor>

      <div class="editor-quill__actions quill-actions">
        <div
          v-if="attachments.length > 0"
          class="quill-actions__attachments actions-attachments"
        >
          <HorizontalList :length="attachments.length">
            <template #items>
              <div
                v-for="(attachment, index) in attachments"
                :key="attachment.uuid"
                :data-offset="index"
                class="actions-attachments__item attachements-item"
              >
                <span
                  class="attachements-item__icon item-icon"
                  :class="{ 'item-icon--img': isFileAnImage(attachment) }"
                />
                <span class="attachements-item__text item-text">{{ attachment.name }}</span>
                <span
                  class="attachements-item__remove item-remove"
                  @click="removeAttachment(Number(index))"
                />
              </div>
            </template>
          </HorizontalList>
        </div>

        <Button
          v-if="showSubmit"
          variant="primary"
          class="quill-actions__submit actions-submit"
          :disabled="!canSubmit"
          @click="submit"
        >
          <span class="actions-submit__content submit-content" />
        </button>
      </div>
    </div>

    <div v-if="hasCounter" class="content-vue-editor__counter editor-counter">
      {{ transchoice('global.char_counter', { count: counter }) }}
      <template v-if="minCounter != undefined">
        • {{ trans('global.min_counter', { count: minCounter }) }}
      </template>
      <template v-if="maxCounter != undefined">
        • {{ trans('global.max_counter', { count: maxCounter }) }}
      </template>
    </div>
  </div>
</template>
